import React, { useEffect, useState } from 'react'
import { useIntl } from 'react-intl'
import { GET_INSTRUCTORS_BY_PROGRAM_ID } from '@queries/getInsructorsByProgramId'
import { InstructorSearchResultInfo, InstructorsQueryResult } from '@interfaces/Instructor'
import { useLazyQuery } from '@apollo/react-hooks'
import InstructorProfile from '@classes/InstructorProfile'
import RebookModal from '@berlitzplatforms/micro.modal.rebook'
import { BlockedProvider, FaveProvider, useFave } from '../Instructors/context'
import { isEmpty, sortBy } from 'lodash'
import Router from 'next/router'
import BerlitzModal from '@berlitzplatforms/micro.ui.modal'
import NotEnoughLessonsRemaining from '../ScheduleOptions/NotEnoughLessonsRemaining'
import { TextAlign } from '@berlitz/globals'
import { RebookH1 } from './style'
import { SessionInfo } from './interface'
import PortalProgram from '@classes/Program'

export interface RebookInstructorProps {
  open: boolean
  onClose: () => void
  StudentId: string
  StudentProgram: PortalProgram
  InstructorId: string
  SessionInfo: SessionInfo
  onFetchError?: () => void
}

export interface IPModal extends Omit<RebookInstructorProps, 'StudentId'> {
  fetching?: boolean
  instructorName?: string
  languages?: string
  onFave?: (isFaved?: boolean) => void
  Instructors: InstructorSearchResultInfo[]
  InstructorAvatar?: string
}

const Modal: React.FC<IPModal> = ({
  open,
  onClose,
  fetching,
  instructorName = '',
  languages = '',
  onFave = () => {},
  InstructorId = '',
  SessionInfo,
  Instructors = [],
  InstructorAvatar = '',
  StudentProgram,
}) => {
  const intl = useIntl()
  const fave = useFave()
  const isFaved = fave.isFaved(InstructorId)
  const isRebookLessonDisabled = StudentProgram?.isRebookLessonDisabled(SessionInfo.NoOfLessons)

  const goToInstructor = (tab: string) => {
    onClose()
    Router.push(
      `/scheduling/instructors/${tab}?instructorId=${InstructorId}&programId=${SessionInfo?.ProgramId}&sessionId=${SessionInfo?.SessionId}`
    )
  }

  if (isRebookLessonDisabled && !isEmpty(StudentProgram?.program)) {
    return (
      <BerlitzModal open={open} onClose={onClose}>
        <TextAlign align="center">
          <RebookH1 disableMargin>
            {intl.formatMessage({
              id: 'Why can’t I re-book my lesson?',
              defaultMessage: 'Why can’t I re-book my lesson?',
            })}
          </RebookH1>
          <NotEnoughLessonsRemaining
            {...StudentProgram?.program}
            isModal
            isRebook
            NoOfLessons={SessionInfo.NoOfLessons}
            withViewCaseBtn={false}
          />
        </TextAlign>
      </BerlitzModal>
    )
  }

  return (
    <RebookModal
      open={open}
      onClose={onClose}
      loading={fetching}
      title={intl.formatMessage(
        {
          id: 'Book {instructorName} again for your next lesson?',
          defaultMessage: 'Book {instructorName} again for your next lesson?',
        },
        {
          instructorName,
        }
      )}
      instructorAvatar={InstructorAvatar}
      instructorName={instructorName}
      instructorDesc={
        languages
          ? `${intl.formatMessage({
              id: 'Speaks',
              defaultMessage: 'Speaks',
            })} ${languages}`
          : ''
      }
      instructorIsFaved={isFaved}
      searchLabel={intl.formatMessage({
        id: 'Search instructors',
        defaultMessage: 'Search instructors',
      })}
      onInstructorClick={() => goToInstructor('profile')}
      onFave={() => {
        const currInstructor = Instructors.find(({ InstructorId: Id }) => Id === InstructorId)
        const newFaves = fave.faveInstructors.filter(
          ({ InstructorId }) => InstructorId !== currInstructor?.InstructorId
        )

        if (currInstructor) {
          if (isFaved) {
            fave.setFaveInstructors(newFaves)
            fave.deleteRelatedInstructors({
              variables: fave.getPayload('delete', [currInstructor]),
            })
          } else {
            fave.setFaveInstructors(sortBy([...newFaves, currInstructor], ['Name']))
            fave.putRelatedInstructors({
              variables: fave.getPayload('put', [currInstructor]),
            })
          }

          onFave(!isFaved)
        }
      }}
      onBook={() => goToInstructor('availability')}
      onSearch={() => goToInstructor('')}
    />
  )
}

const RebookInstructor: React.FC<RebookInstructorProps> = ({
  open,
  onClose,
  StudentId = '',
  StudentProgram,
  InstructorId = '',
  SessionInfo,
  onFetchError = () => {},
}) => {
  const [Instructor, setInstructor] = useState<InstructorProfile | undefined>()
  const [AllInstructors, setAllInstructors] = useState<InstructorSearchResultInfo[]>([])
  const [faveInstructor, setFaveInstructors] = useState<InstructorSearchResultInfo[]>([])
  const [blockedInstructors, setBlockedInstructors] = useState<InstructorSearchResultInfo[]>()
  const [getInstructors, { error, loading: fetching, data }] = useLazyQuery<InstructorsQueryResult>(
    GET_INSTRUCTORS_BY_PROGRAM_ID,
    { fetchPolicy: 'no-cache' }
  )
  const isRebookLessonDisabled = StudentProgram.isRebookLessonDisabled(SessionInfo.NoOfLessons)

  useEffect(() => {
    if (open && StudentId && StudentProgram?.ProgramId && !isRebookLessonDisabled) {
      getInstructors({
        variables: {
          query: {
            StudentProfileId: StudentId,
            ProgramId: StudentProgram?.ProgramId,
          },
        },
      })
    }
  }, [open, StudentId, StudentProgram?.ProgramId, isRebookLessonDisabled])

  useEffect(() => {
    const Instructors = (data?.getInstructorsByProgramId || []).map(({ instructorInfo }) => instructorInfo)
    setAllInstructors(Instructors)
    setFaveInstructors(Instructors.filter(({ IsPreferred }) => IsPreferred))
    setBlockedInstructors(Instructors.filter((item) => item.IsBlocked && !item.Name?.match(/^#Test/)))

    if (Instructors.length) {
      const currInstructor = Instructors.find(({ InstructorId: Id }) => Id === InstructorId)
      currInstructor && setInstructor(new InstructorProfile(currInstructor))
    }
  }, [data])

  useEffect(() => {
    if (open && error) {
      const timeout = setTimeout(() => {
        onFetchError()
        clearTimeout(timeout)
      }, 300)
    }
  }, [open, error])

  return (
    <FaveProvider faveInstructors={faveInstructor} programId={StudentProgram?.ProgramId || ''} studentId={StudentId}>
      <BlockedProvider
        studentId={StudentId}
        programId={StudentProgram?.ProgramId || ''}
        blockedInstructors={blockedInstructors || []}
        rawInstructors={AllInstructors}
      >
        <Modal
          open={open}
          onClose={onClose}
          fetching={fetching}
          instructorName={Instructor?.Name || ''}
          languages={Instructor?.Languages.join(', ')}
          onFave={(IsPreferred) => {
            const newAllInstructors = AllInstructors.map((i) =>
              i.InstructorId === InstructorId ? { ...i, IsPreferred } : i
            )
            setFaveInstructors(newAllInstructors.filter(({ IsPreferred }) => IsPreferred))
            setAllInstructors(newAllInstructors)
          }}
          InstructorId={InstructorId}
          SessionInfo={SessionInfo}
          Instructors={AllInstructors}
          InstructorAvatar={Instructor?.Avatar}
          StudentProgram={StudentProgram}
        />
      </BlockedProvider>
    </FaveProvider>
  )
}

export default RebookInstructor
